import { useReduxState } from 'hooks';
import { getAttributeConditionsString } from 'lib/customAttributes';
import { MoreOptions } from 'components/common';
import {
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeTranslationsInfo } from 'components/common/customAttributes';
import {
  UserAttributesOptionsAdd,
  UserAttributesOptionsAddGroup,
  UserAttributesOptionsDelete,
  UserAttributesOptionsDeleteGroup,
  UserAttributesOptionsEdit,
  UserAttributesOptionsRenameKey,
  UserAttributesOptionsSettings,
  UserAttributesOptionsSort
} from '.';

import type { UserCustomAttribute } from 'shared-types';

interface Props {
  attribute: UserCustomAttribute;
  availableAttributes: {
    [key: string]: UserCustomAttribute;
  };
  isDisabled?: boolean;
  isLimited?: boolean;
  updateData: () => Promise<void>;
}

const UserAttributesOptions = ({
  attribute,
  availableAttributes,
  isDisabled,
  isLimited,
  updateData
}: Props) => {
  const { language, messages, theme, user } = useReduxState();

  const hasMultipleOptionGroups = attribute.optionGroups?.length > 1;

  return (
    <div className="option-groups">
      {attribute.optionGroups?.map((optionGroup, optionGroupIndex) => (
        <div
          className={`option-group ${
            hasMultipleOptionGroups ? 'option-group-multiple' : ''
          }`}
          key={optionGroupIndex}
        >
          <div className="option-group-header">
            <div className="option-group-header-main">
              <div className="option-group-header-title">
                {hasMultipleOptionGroups
                  ? `${messages.commonOptionGroup} ${optionGroupIndex + 1}`
                  : messages.commonOptions}
              </div>
              {optionGroup.conditions?.length ? (
                <div className="option-group-header-conditions">
                  {getAttributeConditionsString({
                    conditions: optionGroup.conditions,
                    language,
                    messages,
                    userCustomAttributesByKey: availableAttributes
                  })}
                </div>
              ) : null}
            </div>

            {!isLimited && !isDisabled ? (
              <div className="option-group-header-actions">
                <UserAttributesOptionsSettings
                  attribute={attribute}
                  availableAttributes={availableAttributes}
                  optionGroupIndex={optionGroupIndex}
                  updateData={updateData}
                />

                {hasMultipleOptionGroups ? (
                  <MoreOptions category="light" styleContentButtons={false}>
                    <UserAttributesOptionsDeleteGroup
                      attribute={attribute}
                      optionGroupIndex={optionGroupIndex}
                      updateData={updateData}
                    />
                  </MoreOptions>
                ) : null}
              </div>
            ) : null}
          </div>

          {!isDisabled ? (
            <div className="option-group-subheader">
              <div className="option-group-footer">
                <UserAttributesOptionsAdd
                  attribute={attribute}
                  optionGroupIndex={optionGroupIndex}
                  updateData={updateData}
                />

                {optionGroup.sort === 'manual' ? (
                  <UserAttributesOptionsSort
                    attribute={attribute}
                    optionGroupIndex={optionGroupIndex}
                    updateData={updateData}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="option-group-options">
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{'#'}</BoxTableCell>
                <BoxTableCell>{messages.commonTitle}</BoxTableCell>
                <BoxTableCell>
                  {optionGroup.customKeyNameTranslations?.[language] ||
                    optionGroup?.customKeyName ||
                    messages.attributesKey}
                </BoxTableCell>
                {optionGroup.enableTranslations ? (
                  <BoxTableCell>
                    {messages.settingsLanguageVariants}
                  </BoxTableCell>
                ) : null}
                <BoxTableCell></BoxTableCell>
              </BoxTableHeader>

              {optionGroup.options?.map((option, optionIndex) => (
                <BoxTableRow key={optionIndex}>
                  <BoxTableCell shrink={true}>
                    <div className="option-number">{optionIndex + 1}</div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <div className="option-label">{option.label}</div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <div className="option-key">{option.key}</div>
                  </BoxTableCell>
                  {optionGroup.enableTranslations ? (
                    <BoxTableCell>
                      <AttributeTranslationsInfo
                        translations={option.labelTranslations}
                      />
                    </BoxTableCell>
                  ) : null}
                  <BoxTableCell shrink={true}>
                    {!isDisabled ? (
                      <MoreOptions styleContentButtons={false}>
                        <UserAttributesOptionsEdit
                          attribute={attribute}
                          optionGroupIndex={optionGroupIndex}
                          optionIndex={optionIndex}
                          updateData={updateData}
                        />
                        {user?.roles?.includes('superadmin') ? (
                          <UserAttributesOptionsRenameKey
                            attribute={attribute}
                            optionGroupIndex={optionGroupIndex}
                            optionIndex={optionIndex}
                            updateData={updateData}
                          />
                        ) : null}
                        <UserAttributesOptionsDelete
                          attribute={attribute}
                          optionGroupIndex={optionGroupIndex}
                          optionIndex={optionIndex}
                          updateData={updateData}
                        />
                      </MoreOptions>
                    ) : null}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </div>

          {!isDisabled ? (
            <div className="option-group-footer">
              <UserAttributesOptionsAdd
                attribute={attribute}
                optionGroupIndex={optionGroupIndex}
                updateData={updateData}
              />

              {optionGroup.sort === 'manual' ? (
                <UserAttributesOptionsSort
                  attribute={attribute}
                  optionGroupIndex={optionGroupIndex}
                  updateData={updateData}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ))}

      {!isLimited && !isDisabled ? (
        <div className="add-group">
          <UserAttributesOptionsAddGroup
            attribute={attribute}
            updateData={updateData}
          />
        </div>
      ) : null}

      <style jsx>{`
        .option-groups {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .option-group {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
        }
        .option-group-header {
          align-items: center;
          border-bottom: 1px solid ${theme.lightGrey};
          display: flex;
          gap: 20px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .option-group-header-main {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .option-group-header-title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
        }
        .option-group-header-conditions {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 400;
        }
        .option-group-header-actions {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .option-group-footer {
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          gap: 10px;
          padding: 10px 15px;
        }
        .option-group-options {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .option {
          align-items: center;
          display: flex;
          gap: 20px;
          padding: 8px 15px;
        }
        .option:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .option-number {
          color: ${theme.textColorLighter};
        }
        .option-label {
          color: ${theme.textColorLight};
          flex-grow: 1;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .option-key {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
        }
        .add-group {
          display: flex;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default UserAttributesOptions;
